export type DuitNowIdType = '01' | '02' | '03' | '04' | '05';
export type DuitNowFlow = 'consent' | 'id';

/** The two PaymentConsent.method values that mean "this row is a DuitNow row". */
export const DUITNOW_METHODS = ['duitnow_consent', 'duitnow_id'] as const;

/** Customer-facing name of both DuitNow methods, wherever a saved method is named in text. */
export const DUITNOW_AUTODEBIT_LABEL = 'DuitNow AutoDebit';

export const isDuitNowMethod = (method: string): boolean =>
  (DUITNOW_METHODS as readonly string[]).includes(method);
export type DuitNowSelection =
  | { duitnow: 'consent'; bankCode: string }
  | {
      duitnow: 'id';
      idType: DuitNowIdType;
      idValue: string;
      countryCode?: string;
    };

export interface DuitNowBank {
  code: string;
  name: string;
}

/**
 * Banks Fiuu accepts for a bank consent (BIC), alphabetical by name — the same list, in the
 * same order, as the backend allowlist. `AFBQMYK` (MBSB, 7 characters) and `BSNAMYK1` (BSN) are
 * Fiuu's own values, not typos to fix.
 */
export const DUITNOW_BANKS: readonly DuitNowBank[] = [
  { code: 'PHBMMYKL', name: 'Affin Bank Berhad' },
  { code: 'AGOBMYKL', name: 'Agrobank' },
  { code: 'MFBBMYKL', name: 'Alliance Bank Malaysia Berhad' },
  { code: 'RJHIMYKL', name: 'Al-Rajhi' },
  { code: 'ARBKMYKL', name: 'Ambank Malaysia Berhad' },
  { code: 'BIMBMYKL', name: 'Bank Islam Malaysia Berhad' },
  { code: 'BKRMMYKL', name: 'Bank Kerjasama Rakyat Malaysia Berhad' },
  { code: 'BMMBMYKL', name: 'Bank Muamalat Malaysia Bhd' },
  { code: 'BOFAMY2X', name: 'Bank of America (M) Berhad' },
  { code: 'BKCHMYKL', name: 'Bank of China (M) Berhad' },
  { code: 'BSNAMYK1', name: 'Bank Simpanan Nasional Berhad' },
  { code: 'BNPAMYKL', name: 'BNP Paribas Malaysia Berhad' },
  { code: 'PCBCMYKL', name: 'China Construction Bank (Malaysia) Berhad' },
  { code: 'CIBBMYKL', name: 'CIMB Bank Berhad' },
  { code: 'CITIMYKL', name: 'Citibank Berhad' },
  { code: 'DEUTMYKL', name: 'Deutsche Bank (Malaysia) Berhad' },
  { code: 'HLBBMYKL', name: 'Hong Leong Bank Berhad' },
  { code: 'HBMBMYKL', name: 'HSBC Bank Malaysia Berhad' },
  {
    code: 'ICBKMYKL',
    name: 'Industrial and Commercial Bank of China (M) Berhad'
  },
  { code: 'CHASMYKX', name: 'JP Morgan Chase Bank Berhad' },
  { code: 'KFHOMYKL', name: 'Kuwait Finance House' },
  { code: 'MBBEMYKL', name: 'Maybank Berhad' },
  { code: 'AFBQMYK', name: 'MBSB Bank Berhad' },
  { code: 'MHCBMYKA', name: 'Mizuho Bank (Malaysia) Berhad' },
  { code: 'BOTKMYKX', name: 'MUFG Bank (Malaysia) Berhad' },
  { code: 'OCBCMYKL', name: 'OCBC Bank Berhad' },
  { code: 'PBBEMYKL', name: 'Public Bank Berhad' },
  { code: 'RHBBMYKL', name: 'RHB Bank Berhad' },
  { code: 'SCBLMYKX', name: 'Standard Chartered Bank Malaysia Berhad' },
  { code: 'SMBCMYKL', name: 'Sumitomo Mitsui Banking Corporation (M) Berhad' },
  { code: 'UOVBMYKL', name: 'United Overseas Bank Berhad (UOB)' }
];

/**
 * Banks a new DuitNow bank consent can be started with today. Every other bank in
 * DUITNOW_BANKS stays listed, disabled, as coming soon. user-portal-backend refuses the same
 * set when a consent is started.
 */
export const DUITNOW_AVAILABLE_BANK_CODES: ReadonlySet<string> = new Set([
  'ARBKMYKL', // Ambank Malaysia Berhad
  'BIMBMYKL', // Bank Islam Malaysia Berhad
  'BMMBMYKL', // Bank Muamalat Malaysia Bhd
  'BKCHMYKL', // Bank of China (M) Berhad
  'CIBBMYKL', // CIMB Bank Berhad
  'HLBBMYKL', // Hong Leong Bank Berhad
  'MBBEMYKL', // Maybank Berhad
  'RHBBMYKL', // RHB Bank Berhad
  'UOVBMYKL' // United Overseas Bank Berhad (UOB)
]);

export const isDuitNowBankAvailable = (code: string): boolean =>
  DUITNOW_AVAILABLE_BANK_CODES.has(code);

export const findDuitNowBank = (
  code: string | null | undefined
): DuitNowBank | undefined => {
  const c = (code ?? '').trim().toUpperCase();
  return c ? DUITNOW_BANKS.find((b) => b.code === c) : undefined;
};

/**
 * The `/pay` body fields that describe a DuitNow selection. Shared by the registration and home
 * flows so both send the same shape; every key is `undefined` when it does not apply.
 */
export const duitNowPayFields = (sel: DuitNowSelection | null | undefined) => ({
  payment_method: sel
    ? sel.duitnow === 'consent'
      ? ('duitnow_consent' as const)
      : ('duitnow_id' as const)
    : undefined,
  duitnow_id_type: sel?.duitnow === 'id' ? sel.idType : undefined,
  duitnow_id_value: sel?.duitnow === 'id' ? sel.idValue : undefined,
  // the DuitNow mobile's own dial code (the country list is open); PhoneNumberInput strips it from the number
  duitnow_id_country_code:
    sel?.duitnow === 'id' && sel.idType === '05' ? sel.countryCode : undefined,
  duitnow_bank_code: sel?.duitnow === 'consent' ? sel.bankCode : undefined
});

/** Order and labels follow the DuitNow ID screen design. */
export const DUITNOW_ID_OPTIONS = [
  {
    type: '05',
    label: 'Mobile number',
    fieldLabel: 'Phone Number',
    placeholder: '1234567890',
    icon: 'phone'
  },
  {
    type: '01',
    label: 'NRIC',
    fieldLabel: 'NRIC',
    placeholder: '991230-08-1234',
    icon: 'user'
  },
  {
    type: '03',
    label: 'Passport',
    fieldLabel: 'Passport No.',
    placeholder: 'A12345678',
    icon: 'id'
  },
  {
    type: '04',
    label: 'Business registration no.',
    fieldLabel: 'Business Registration No.',
    placeholder: '202301012345',
    icon: 'briefcase'
  },
  {
    type: '02',
    label: 'Army / Police no.',
    fieldLabel: 'Army / Police No.',
    placeholder: 'T1234567',
    icon: 'star'
  }
] as const;

export const DUITNOW_ID_ERRORS: Record<DuitNowIdType, string> = {
  '05': 'Enter a valid mobile number.',
  '01': 'Enter a valid NRIC (e.g. 991230-08-1234).',
  '03': 'Enter a valid passport number.',
  '04': 'Enter a valid business registration number.',
  '02': 'Enter a valid army / police number.'
};

/** The line under "DuitNow AutoDebit" on a saved card: the bank, or the DuitNow ID type. */
export const getDuitNowMethodLabel = (method: {
  method: string;
  idTp?: string | null;
  bank_name?: string | null;
}): string => {
  if (method.method === 'duitnow_consent')
    return method.bank_name ?? 'Your bank';
  const option = DUITNOW_ID_OPTIONS.find((opt) => opt.type === method.idTp);
  return option?.label ?? 'DuitNow ID';
};
